import {
  type ApiServiceModelDescriptor,
  type CloudFlowNodeType,
  type Member,
  type MemberReference,
  ModelType,
  type StructureApiServiceModelDescriptor,
  type UnwrappedApiServiceModelDescriptor,
  type Variables,
} from "@doitintl/cmp-models";
import { type WithFirebaseModel } from "@doitintl/models-admin";

import { calculateConditionalNodeOutputModel } from "./calculated-node-output-models/conditional-node";
import { calculateDateTransformationNodeOutputModel } from "./calculated-node-output-models/date-transform-node";
import { calculateTransformationNodeOutputModel } from "./calculated-node-output-models/transformation-node";
import { getModelDescriptorFromObject } from "./parser";
import {
  isActionNode,
  isConditionNode,
  isDateTransformationNode,
  isFilterNode,
  isSystemEventTriggerNode,
  isTransformationNode,
  isTriggerNode,
  isWebhookTriggerNode,
} from "./pattern-matchers";
import { type NodeModelWithId } from "./types";

export class ReferencedNodeNotFoundException extends Error {
  public readonly name = "ReferencedNodeNotFoundException";

  constructor(nodeId: string) {
    super(`Could not find referenced node id: ${nodeId}`);
  }
}

export class ModelNotFoundException extends Error {
  public readonly name = "ModelNotFoundException";
}

export type ModelPath = string[];

export type GetOutputModelFromActionNodeFn = (
  referencedNode: NodeModelWithId<CloudFlowNodeType.ACTION>
) => Promise<UnwrappedApiServiceModelDescriptor | null>;

export type GetOutputModelFromSystemEventTriggerNodeFn = (
  referencedNode: NodeModelWithId<CloudFlowNodeType.SYSTEM_EVENT_TRIGGER>
) => Promise<UnwrappedApiServiceModelDescriptor | null>;

export type OutputModelFetcher = {
  getOutputModelFromActionNode: GetOutputModelFromActionNodeFn;
  getOutputModelFromSystemEventTriggerNode: GetOutputModelFromSystemEventTriggerNodeFn;
};

function getTriggerCommonModel(variables?: Variables): StructureApiServiceModelDescriptor<Member> {
  const members: Record<string, { model: UnwrappedApiServiceModelDescriptor }> = {
    startTime: { model: { type: ModelType.TIMESTAMP, timestampFormat: "x" } },
  };

  // Only add variables if they exist and are not empty
  if (variables && (variables.globalVariables.length > 0 || variables.localVariables.length > 0)) {
    const transformedVariables: Record<string, any> = {};

    if (variables.globalVariables.length > 0) {
      transformedVariables.globalVariables = Object.fromEntries(
        variables.globalVariables.map((variable) => [variable.id, variable.value])
      );
    }

    if (variables.localVariables.length > 0) {
      transformedVariables.localVariables = Object.fromEntries(
        variables.localVariables.map((variable) => [variable.id, variable.value])
      );
    }

    const variablesModel = getModelDescriptorFromObject(transformedVariables);
    members.variables = { model: variablesModel };
  }

  return {
    type: ModelType.STRUCTURE,
    members,
  };
}

export async function getNodeOutputModel(
  outputModelFetcher: OutputModelFetcher,
  nodes: NodeModelWithId[],
  nodeId: string,
  variables?: Variables
): Promise<UnwrappedApiServiceModelDescriptor | null> {
  const referencedNode = nodes.find(({ id }: { id: string }) => nodeId === id);
  if (referencedNode === undefined) {
    throw new ReferencedNodeNotFoundException(nodeId);
  }

  switch (true) {
    case isSystemEventTriggerNode(referencedNode): {
      const triggerModel = await outputModelFetcher.getOutputModelFromSystemEventTriggerNode(referencedNode);
      const systemEventTriggerModel = getTriggerCommonModel(variables);

      if (triggerModel) {
        systemEventTriggerModel.members.triggerPayload = {
          model: triggerModel,
        };
      }

      return wrapModelWithListModel(systemEventTriggerModel);
    }

    case isWebhookTriggerNode(referencedNode): {
      let triggerModel: UnwrappedApiServiceModelDescriptor | null = null;
      try {
        triggerModel = getModelDescriptorFromObject(JSON.parse(referencedNode.parameters.payloadModel));
      } catch {
        // do nothing
      }

      const webhookTriggerModel = getTriggerCommonModel(variables);

      if (triggerModel) {
        webhookTriggerModel.members.triggerPayload = {
          model: triggerModel,
        };
      }

      return wrapModelWithListModel(webhookTriggerModel);
    }
    case isTriggerNode(referencedNode):
      return wrapModelWithListModel(getTriggerCommonModel(variables));
    case isActionNode(referencedNode):
      return outputModelFetcher.getOutputModelFromActionNode(referencedNode);
    case isConditionNode(referencedNode):
    case isFilterNode(referencedNode): {
      return calculateConditionalNodeOutputModel(referencedNode, nodes, outputModelFetcher);
    }
    case isTransformationNode(referencedNode): {
      return calculateTransformationNodeOutputModel(referencedNode, nodes, outputModelFetcher);
    }
    case isDateTransformationNode(referencedNode): {
      return calculateDateTransformationNodeOutputModel(referencedNode, nodes, outputModelFetcher);
    }
  }

  throw new Error(`Unable to provide an output model for node ${referencedNode.id} with type ${referencedNode.type}`);
}

export function getModelRequiredTupleByPath(
  model: UnwrappedApiServiceModelDescriptor,
  path: ModelPath,
  required = false
): [UnwrappedApiServiceModelDescriptor, boolean] {
  if (path.length === 0) {
    return [model, required];
  }

  switch (model.type) {
    case ModelType.LIST:
      return getModelRequiredTupleByPath(model.member.model, path);
    case ModelType.STRUCTURE: {
      const [memberName, ...pathRest] = path;
      const memberModel = model.members[memberName];
      if (memberModel === undefined) {
        throw new ModelNotFoundException(`Could not get model for path token ${memberName}`);
      }

      return getModelRequiredTupleByPath(memberModel.model, pathRest);
    }
    case ModelType.MAP: {
      const [, ...pathRest] = path;
      return getModelRequiredTupleByPath(model.valueMember.model, pathRest);
    }
    default:
      throw new ModelNotFoundException("Model path out of bounds!");
  }
}

export function getModelByPath(model: UnwrappedApiServiceModelDescriptor, path: ModelPath) {
  const [outputModel] = getModelRequiredTupleByPath(model, path);
  return outputModel;
}

export function wrapModelWithListModel(model: UnwrappedApiServiceModelDescriptor): UnwrappedApiServiceModelDescriptor {
  if (model.type === ModelType.LIST) {
    return model;
  }
  return {
    type: ModelType.LIST,
    member: {
      model,
    },
  };
}

export function isUnwrappedMember(member: Member | MemberReference): member is Member {
  return Object.hasOwn(member, "model");
}

// at the moment we limit the depth of recursion to 5 to avoid exponential memory consumption
const MAX_DEPTH_OF_RECURSION = 6;

async function doModelUnwrap(
  getReferencedModelById: (modelId: string) => Promise<WithFirebaseModel<ApiServiceModelDescriptor>>,
  modelToUnwrap: ApiServiceModelDescriptor,
  trail: string[]
): Promise<WithFirebaseModel<UnwrappedApiServiceModelDescriptor>> {
  if (hasCircuitsWithLength(trail, MAX_DEPTH_OF_RECURSION)) {
    return {
      type: ModelType.STRING,
      enum: ["MAX_DEPTH_OF_RECURSION_REACHED"],
      enumDescriptions: [`We support up to ${MAX_DEPTH_OF_RECURSION} levels of recursion`],
    };
  }

  switch (modelToUnwrap.type) {
    case ModelType.LIST: {
      const [memberModel, newTrail] = await unwrapMemberWithTrail(getReferencedModelById, modelToUnwrap.member, trail);
      return {
        ...modelToUnwrap,
        member: {
          documentation: modelToUnwrap.member.documentation,
          model: await doModelUnwrap(getReferencedModelById, memberModel, newTrail),
        },
      };
    }
    case ModelType.STRUCTURE: {
      const membersEntries = await Promise.all(
        Object.entries(modelToUnwrap.members).map(async ([memberName, member]) => {
          const [memberModel, newTrail] = await unwrapMemberWithTrail(getReferencedModelById, member, trail);

          return [
            memberName,
            {
              model: await doModelUnwrap(getReferencedModelById, memberModel, newTrail),
              documentation: member.documentation,
              label: member.label,
            },
          ] as const;
        })
      );
      return {
        ...modelToUnwrap,
        members: Object.fromEntries(membersEntries),
      };
    }
    case ModelType.MAP: {
      const [keyMemberModel, newKeyTrail] = await unwrapMemberWithTrail(
        getReferencedModelById,
        modelToUnwrap.keyMember,
        trail
      );
      const [valueMemberModel, newValueTrail] = await unwrapMemberWithTrail(
        getReferencedModelById,
        modelToUnwrap.valueMember,
        trail
      );

      return {
        ...modelToUnwrap,
        keyMember: {
          model: await doModelUnwrap(getReferencedModelById, keyMemberModel, newKeyTrail),
          documentation: modelToUnwrap.keyMember.documentation,
        },
        valueMember: {
          model: await doModelUnwrap(getReferencedModelById, valueMemberModel, newValueTrail),
          documentation: modelToUnwrap.valueMember.documentation,
        },
      };
    }
    default:
      return modelToUnwrap as WithFirebaseModel<UnwrappedApiServiceModelDescriptor>;
  }
}

async function unwrapMemberWithTrail(
  getReferencedModelById: (modelId: string) => Promise<WithFirebaseModel<ApiServiceModelDescriptor>>,
  member: Member | MemberReference,
  trail: string[]
): Promise<[ApiServiceModelDescriptor, string[]]> {
  if (isUnwrappedMember(member)) {
    return [member.model, trail];
  }
  return [await getReferencedModelById(member.modelId), [...trail, member.modelId]];
}

function hasCircuitsWithLength(trail: string[], length: number) {
  let tortoise = -1;
  let hare = -1;
  let hasCircuit = false;

  while (hare < trail.length && !hasCircuit) {
    tortoise = (tortoise + 1) % trail.length;
    hare = (hare + 2) % trail.length;

    hasCircuit = trail[tortoise] === trail[hare];
  }

  if (!hasCircuit) {
    return false;
  }

  tortoise = 0;
  while (trail[tortoise] !== trail[hare]) {
    tortoise = (tortoise + 1) % trail.length;
    hare = (hare + 2) % trail.length;
  }

  return trail.length - tortoise >= length;
}

export const unwrapModel = async (
  getReferencedModelById: (modelId: string) => Promise<WithFirebaseModel<ApiServiceModelDescriptor>>,
  modelToUnwrap: ApiServiceModelDescriptor
): Promise<WithFirebaseModel<UnwrappedApiServiceModelDescriptor>> =>
  doModelUnwrap(getReferencedModelById, modelToUnwrap, []);

export function isPathRelative(basePath: ModelPath, path: ModelPath) {
  return basePath.every((token, idx) => path[idx] === token);
}

export function getRelativePath(basePath: ModelPath, path: ModelPath) {
  if (!isPathRelative(basePath, path)) {
    throw new ModelNotFoundException(
      `Cannot extract relative path from [${path.join(",")}] with base [${basePath.join(",")}]`
    );
  }
  return path.slice(basePath.length);
}

export function getCommonPath(basePath: ModelPath, path: ModelPath) {
  const index = basePath.findIndex((token, idx) => path[idx] !== token);
  return basePath.slice(0, index);
}
